import {
  getAllDocumentsWithCountFromDB,
  getBlocksWithLinkTargetFromDB,
  getDocumentByPriorityAirlineWithUnderscoreFromDB,
  getDocumentFromDB,
  getDocumentsByPriorityFromDB,
  getDocumentsFromDB,
  getDocumentsOrderedByWeightFromDB,
  getFlightCitiesForMultipleCodeFromDB,
  getIntlCitiesForMultipleCodeFromDB,
  getIntlNonIndiaRoutesFromDB,
  getIfRoutesFromDB,
  getTopAirlineDomesticRoutesFromDB,
  getTopAirlineRoutesFromDB,
} from "./CommonUtils";
import {
  AIRLINE,
  AIRPORT,
  BLOCK,
  CITY,
  COUNTRY,
  DFROUTE,
  DFTemplate,
  FLIGHTS_NODE_TABLE,
  IFTemplate,
  INTL_AIRLINE,
  INTL_AIRLINE_ROUTES,
  INTL_AIRPORT,
  INTL_CITY,
  INTL_COUNTRY,
  ROUTE_DISTANCE,
  STATIC_PAGES_TEMPLATE,
} from "./Constants";

const ALL_REGIONAL_ABOUT_CITY_FIELDS = [
  'about_src_city_gcc',
  'about_src_city_ksa',
  'about_src_city_global',
  'about_city_united_states',
  'about_city_canada',
  'about_city_singapore',
  'about_city_australia',
  'about_city_united_kingdom',
  'about_city_bahrain',
  'about_city_oman',
  'about_city_qatar',
  'about_city_kuwait',
];

/**
 * Builds a MongoDB exclusion projection for intl_city queries.
 * Always keeps `about_city` as fallback; only includes the regional
 * about_city field relevant to the current domain/country context.
 */
export const getIntlCityExcludeProjection = (dataMap) => {
  if (!dataMap) return null;

  const { currentDomainName, isGoibibo, country_folder } = dataMap;
  const fieldsToKeep = new Set();

  if (!isGoibibo) {
    if (currentDomainName?.includes('ae.makemytrip.global')) {
      fieldsToKeep.add('about_src_city_gcc');
    } else if (currentDomainName?.includes('sa.makemytrip.global')) {
      fieldsToKeep.add('about_src_city_ksa');
    } else if (currentDomainName?.includes('www.makemytrip.global')) {
      if (country_folder) {
        fieldsToKeep.add(`about_city_${country_folder}`);
      }
    }
  }

  const projection = {};
  ALL_REGIONAL_ABOUT_CITY_FIELDS.forEach(field => {
    if (!fieldsToKeep.has(field)) {
      projection[field] = 0;
    }
  });

  return Object.keys(projection).length > 0 ? projection : null;
};

export const getCityByFlightsUrlPhrase = async (urlPhrase) => {
  const city = await getDocumentFromDB(CITY, { flights_url_phrase: urlPhrase });
  return city;
};

export const getDFAirportForCityCode = async (cityCode) => {
  const airport = await getDocumentsFromDB(AIRPORT, { city_code: cityCode });
  return airport;
};

export const getIntlCountryByUrlPhrase = async (urlPhrase) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { url_phrase: urlPhrase });
  return country;
};

export const getIntlCountryByUrlPhraseAndShowCountryPage = async (urlPhrase, showCountryPage) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { url_phrase: urlPhrase });
  if (!country || country?.show_country_page !== showCountryPage) {
    return null;
  }
  return country;
};

export const getIntlCountryByCode = async (countryCode) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { code: countryCode });
  return country;
};

export const getIntlCountryByCodeAndShowCountryPage = async (countryCode, showCountryPage) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, { code: countryCode });
  if (!country || country?.show_country_page !== showCountryPage) {
    return null;
  }
  return country;
};

export const getIntlCityByUrlPhrase = async (urlPhrase, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const city = await getDocumentFromDB(INTL_CITY, { url_phrase: urlPhrase }, projection);
  return city;
};

export const getIntlCityByCityCode = async (cityCode, dataMap = null) => {
  try {
    const projection = getIntlCityExcludeProjection(dataMap);
    const city = await getDocumentFromDB(INTL_CITY, { city_code: cityCode }, projection);
    return city;
  } catch (error) {
    console.error('Error fetching intl city by city code:', error);
    return null;
  }
};

export const getIntlCityByCountryIdWithCount = async (countryCode, order, count, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const city = await getDocumentsByPriorityFromDB(
    INTL_CITY,
    { country_id: countryCode },
    order,
    count,
    projection,
  );
  return city;
};

export const getAirportForCityCode = async (cityCode) => {
  const airport = await getDocumentsFromDB(INTL_AIRPORT, { city_code: cityCode });
  return airport;
};

export const getIntlCityByCityCodeAndCountryId = async (cityCode, countryCode, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const city = await getDocumentFromDB(INTL_CITY, { city_code: cityCode, country_id: countryCode }, projection);
  return city;
};

export const getAirlineByUrlPhrase = async (dataMap, airlineUrlPhrase) => {
  const db = dataMap?.isIntl ? INTL_AIRLINE : AIRLINE;
  const airline = await getDocumentFromDB(db, { url_phrase: airlineUrlPhrase });
  return airline;
};

export const getAirlineByAlUrlPhrase = async (dataMap, airlineUrlPhrase) => {
  const db = dataMap?.isIntl ? INTL_AIRLINE : AIRLINE;
  const airline = await getDocumentFromDB(db, { al_url_phrase: airlineUrlPhrase });
  return airline;
};

export const getTemplateByType = async (dataMap, templateType) => {
  const type = dataMap?.isIntl ? IFTemplate : DFTemplate;
  const template = await getDocumentFromDB(type, { template_type: templateType });
  return template;
};

export const getBlocksForTemplate = async (templateType) => {
  const blocks = await getDocumentsOrderedByWeightFromDB(BLOCK, { content_type: templateType });
  return blocks;
};

export const getBlockByDelta = async (templateType, blockDelta) => {
  const block = await getDocumentsOrderedByWeightFromDB(BLOCK, {
    content_type: templateType,
    delta: blockDelta,
  });
  return block;
};

export const getFlightNodeByURL = async (urlPhrase) => {
  const flightNode = await getDocumentsFromDB(FLIGHTS_NODE_TABLE, { url: urlPhrase });
  return flightNode;
};

export const getIntlCitiesForMultipleCode = async (cityCodes, dataMap = null) => {
  
  // Convert to array if needed and filter out empty values
  const cityCodeArray = Array.isArray(cityCodes) ? cityCodes : [...cityCodes];
  const validCityCodes = cityCodeArray.filter(code => code && code.trim());
  
  if (validCityCodes.length === 0) {
    return [];
  }

  const projection = getIntlCityExcludeProjection(dataMap);
  // Use bulk query API with MongoDB $in operator for much better performance
  const cities = await getIntlCitiesForMultipleCodeFromDB(INTL_CITY, { 
    cityCodeSet: validCityCodes.join(',') 
  }, projection);
  return cities;
};

export const getRandomIntlCityByTopFlagWithCount = async (topFlag, count, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const cities = await getAllDocumentsWithCountFromDB(INTL_CITY, { 
    top_flag: topFlag 
  }, count, projection);
  
  return cities;
};

export const getRouteDistanceBySrcCityCodeAndDestCityCode = async (srcCityCode, destCityCode) => {
  const distance = await getDocumentFromDB(ROUTE_DISTANCE, {
    src_city_code: srcCityCode,
    dest_city_code: destCityCode,
  });
  return distance;
};

export const getCountryDataByUrlPhrase = async (urlPhrase) => {
  const country = await getDocumentFromDB(COUNTRY, { url_phrase: urlPhrase });
  return country;
};

export const getDfAirlineByCode = async (airlineCode) => {
  const airline = await getDocumentFromDB(AIRLINE, { code: airlineCode });
  return airline;
};

export const getAllTopAirlineRoutes = async () => {
  const routes = await getDocumentsFromDB(DFROUTE, { is_airline_top_route: true });
  return routes;
};

export const getTemplateDataByUrlAndLob = async (url, lob) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, { url, lob_type: lob });
  return template;
};

export const getTemplateDataByUrl = async (url) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, { url });
  return template;
};

export const getTemplateDataByAlternateUrlAndLob = async (alternatUrl, lob) => {
  const template = await getDocumentFromDB(STATIC_PAGES_TEMPLATE, {
    alternat_url: alternatUrl,
    lob_type: lob,
  });
  return template;
};

export const getBlocksForTemplateAndUrl = async (templateType, url) => {
  
  const blocks = await getBlocksWithLinkTargetFromDB(BLOCK, {
    contentType: templateType,
    linkTargetUrl: url,
  });
  
  return blocks;
};

export const getCityForCountryByTopFlag = async (countryId, topFlag) => {
  const city = await getDocumentFromDB(CITY, { country_id: countryId });
  let cityReturn = null;
  for (let i = 0; i < city.length; i += 1) {
    if (city.top_flag === topFlag) {
      cityReturn = city;
      break;
    }
  }
  return cityReturn;
};

export const getCitiesForCountryByTopFlag = async (countryId, topFlag) => {
  
  const cities = await getDocumentsFromDB(CITY, { 
    country_id: countryId, 
    top_flag: topFlag 
  });
  
  return cities;
};

export const getCitiesForCountryAndByAirportFlagWithCount = async (
  countryId,
  airportFlag,
  count,
) => {
  const cities = await getAllDocumentsWithCountFromDB(CITY, { 
    country_id: countryId, 
    airport_flag: airportFlag 
  }, count);
  
  return cities;
};

export const getCitiesForCountryAndByAirportFlag = async (countryId, airportFlag) => {

  // OPTIMIZED: Query database with both filters directly - no client-side filtering needed
  const cities = await getDocumentsFromDB(CITY, { 
    country_id: countryId, 
    airport_flag: airportFlag 
  });
  
  return cities;
};

export const getCityByFlightCityCode = async (flightCityCode) => {
  const city = await getDocumentFromDB(CITY, { flight_city_code: flightCityCode });
  return city;
};

export const getByLob = async (lob) => {
  const textToLinkDictionary = await getDocumentsFromDB("text_to_link_mapping", { lob });
  return textToLinkDictionary;
};

export const getReviewsForSrcDest = async (src, dest) => {
  const reviews = await getDocumentsFromDB("flight_reviews", { src, dest });
  return reviews;
};

export const getIFCityByUrlPhrase = async (urlPhrase, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const city = await getDocumentFromDB(INTL_CITY, { url_phrase: urlPhrase }, projection);
  return city;
};

export const getAirlinesByTopFlag = async (topFlag) => {
  const airlines = await getDocumentsFromDB(AIRLINE, { top_flag: topFlag });
  return airlines;
};

export const getAirlinesByTopFlagWithCount = async (topFlag, count) => {

  const airlines = await getAllDocumentsWithCountFromDB(AIRLINE, { 
    top_flag: topFlag 
  }, count);
  
  return airlines;
};

export const getIntlAirlinesByTopFlagWithCount = async (topFlag, count) => {

  const airlines = await getAllDocumentsWithCountFromDB(INTL_AIRLINE, { top_flag: topFlag }, count);
  return airlines;
};

export const getAirlineRoutesForPriorityWithCount = async (airlineCode, priority, count) => {
  const airlineFieldName = `priority_${airlineCode}`;
  const dfRoutes = await getAllDocumentsWithCountFromDB(DFROUTE, { [airlineFieldName]: priority }, count);
  return dfRoutes;
};

export const getCityByCityCode = async (cityCode) => {
  const city = await getDocumentFromDB(CITY, { city_code: cityCode });
  return city;
};

export const findByName = async (name) => {
  const responses = await getDocumentsFromDB("acg", { name });
  return responses;
};

export const getAllTopAirlineRoutesToCity = async (cityCode, flag) => {
  const routes = await getDocumentsFromDB(DFROUTE, {
    dest_city_code: cityCode,
    is_airline_top_route: flag,
  });
  return routes;
};

export const getRouteFromCityWithCount = async (cityCode, count) => {
  const routes = await getDocumentsByPriorityFromDB(
    DFROUTE,
    { src_city_code: cityCode },
    "ASC",
    count,
  );
  return routes;
};

export const getRouteToCityWithCount = async (cityCode, count) => {
  const routes = await getDocumentsByPriorityFromDB(
    DFROUTE,
    { dest_city_code: cityCode },
    "ASC",
    count,
  );
  return routes;
};

export const getAllAirlines = async () => {
  const airlines = await getDocumentsFromDB(AIRLINE, {});
  return airlines;
};

export const getAirportByAirportUrlPhrase = async (airportUrlPhrase) => {
  const aiports = await getDocumentsFromDB(AIRPORT, { url_phrase: airportUrlPhrase });
  return aiports;
};

export const getCitiesByFlightCityCode = async (cityCode) => {
  const cities = await getDocumentsFromDB(CITY, { flight_city_code: cityCode });
  return cities;
};

export const getAirlineByCode = async (airlineCode) => {
  const airline = await getDocumentFromDB(AIRLINE, { code: airlineCode });
  return airline;
};

export const getIntlCityByLocusCityCode = async (locusCityCode, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const city = await getDocumentsFromDB(INTL_CITY, { locus_city_code: locusCityCode }, projection);
  return city;
};

export const getIntlCityByTopFlagWithCount = async (topFlag, count, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const cities = await getAllDocumentsWithCountFromDB(INTL_CITY, { top_flag: topFlag }, count, projection);
  return cities;
};

export const getRoutesFromCityWithTopFlagAndCountAndType = async (
  srcCityUrlPhrase,
  topFlag,
  type,
  count,
) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_url_phrase: srcCityUrlPhrase,
    top_flag: topFlag,
    type,
  }, count);
  return intlRoutes;
};

export const getIntlRouteByTopFlagWithCount = async (code, count) => {
  const routes = await getAllDocumentsWithCountFromDB(INTL_AIRLINE_ROUTES, { airline_code: code }, count);
  return routes;
};

export const getIntlAirlineByCode = async (code) => {
  const airline = await getDocumentFromDB(INTL_AIRLINE, { code });
  return airline;
};

export const getRoutesFromCityWithCountryIdAndTopFlagAndCountAndType = async (
  srcCityUrlPhrase,
  countryId,
  topFlag,
  type,
  count,
) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_url_phrase: srcCityUrlPhrase,
    dest_country_id: countryId,
    top_flag: topFlag,
    type,
  }, count);
  return intlRoutes;
};

export const getIntlAirlineRouteFromSrcToDestWithCount = async (
  srcUrlPhrase,
  destUrlPhrase,
  count,
) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB(INTL_AIRLINE_ROUTES, {
    src_url_phrase: srcUrlPhrase,
    dest_url_phrase: destUrlPhrase,
  }, count);
  return intlRoutes;
};

export const getRoutesFromSpecificCityToTopCitiesOfCountryWithCount = async (
  srcCityUrlPhrase,
  destCountryCode,
  count,
) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_url_phrase: srcCityUrlPhrase,
    dest_country_id: destCountryCode,
  }, count);
  return intlRoutes;
};

export const getRoutesBetweenCountriesWithCount = async (
  srcCountryID,
  destCountryId,
  type,
  count,
) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_country_id: srcCountryID,
    dest_country_id: destCountryId,
    type,
  }, count);
  return intlRoutes;
};

export const getRoutesBetweenCountries = async (srcCountryID, destCountryId, count) => {
  const intlRoutes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_country_id: srcCountryID,
    dest_country_id: destCountryId,
  }, count);
  return intlRoutes;
};

export const getAirlineSpecificRoutesWithCount = async (
  count,
  priorityAirlineWithoutUnderscore,
) => {
  const airlineDomRoutes = await getDocumentByPriorityAirlineWithUnderscoreFromDB(
    "dfroutes",
    count,
    priorityAirlineWithoutUnderscore,
  );
  return airlineDomRoutes;
};

export const findByCountryId = async (countryId, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const cities = await getDocumentsFromDB(INTL_CITY, { country_id: countryId }, projection);
  return cities;
};

export const getIntlNonIndiaRouteWithCityCodeAndCount = async (
  srcCityCode,
  excludeIndianCityCodes,
  count,
) => {
  
  // Use optimized MongoDB aggregation pipeline for filtering
  const routes = await getIntlNonIndiaRoutesFromDB(INTL_AIRLINE_ROUTES, {
    srcCityCode,
    excludeCityCodes: excludeIndianCityCodes.join(','),
    count,
  });
  
  return routes;
};

export const getTopIntlRouteFromSrcWithCount = async (count) => {
  const routes = await getAllDocumentsWithCountFromDB(INTL_AIRLINE_ROUTES, {}, count);
  return routes;
};

export const getTopRoutesFromDestination = async (cityCode, count) => {
  const routes = await getAllDocumentsWithCountFromDB(INTL_AIRLINE_ROUTES, { dest_city_code: cityCode }, count);
  return routes;
};

export const getRoutesToCityWithTopFlagAndCountAndType = async (
  destCityUrlPhrase,
  topFlag,
  type,
  count,
) => {
  const routes = await getAllDocumentsWithCountFromDB("intl_route", {
    dest_url_phrase: destCityUrlPhrase,
    top_flag: topFlag,
    type,
  }, count);
  return routes;
};

export const getIFRoutes = async (isIntl, sectors) => {
  const collection = isIntl ? "intl_route" : "dfroutes";
  let routes = [];
  if (sectors?.length > 0) {
    routes = await getIfRoutesFromDB(collection, { sectors: JSON.stringify(sectors) });
  }
  return routes;
};

export const getRoutesFromCountryWithTopFlagTypeAndPriorityWithCount = async (
  countryID,
  topFlag,
  type,
  priority,
  count,
) => {
  const routes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_country_id: countryID,
    top_flag: topFlag,
    type,
    priority,
  }, count);
  return routes;
};

export const getRoutesFromCountryWithTopFlagTypeWithCount = async (
  countryID,
  topFlag,
  type,
  count,
) => {
  const routes = await getAllDocumentsWithCountFromDB("intl_route", {
    src_country_id: countryID,
    top_flag: topFlag,
    type,
  }, count);
  return routes;
};

export const getRoutesToCountryWithTopFlagTypeWithCount = async (
  countryID,
  topFlag,
  type,
  count,
) => {
  const routes = await getAllDocumentsWithCountFromDB("intl_route", {
    dest_country_id: countryID,
    top_flag: topFlag,
    type,
  }, count);
  return routes;
};

export const getIntlCityByTopFlagAndCountryIdWithCount = async (topFlag, countryId, count, dataMap = null) => {
  const projection = getIntlCityExcludeProjection(dataMap);
  const cities = await getAllDocumentsWithCountFromDB(INTL_CITY, { top_flag: topFlag, country_id: countryId }, count, projection);
  return cities;
};

export const getTopAirlineRoutes = async (code, countryCode, count) => {
  const routes = await getTopAirlineRoutesFromDB(INTL_AIRLINE_ROUTES, {
    airline_code: code,
    src_country_id: countryCode,
    dest_country_id: countryCode,
    count,
  });
  return routes;
};

export const getTopAirlineDomesticRoutes = async (code, countryCode, count) => {
  const routes = await getTopAirlineDomesticRoutesFromDB(INTL_AIRLINE_ROUTES, {
    airline_code: code,
    src_country_id: countryCode,
    dest_country_id: countryCode,
    count,
  });
  return routes;
};

export const getFlightCitiesForMultipleCode = async (codes) => {
  const cities = await getFlightCitiesForMultipleCodeFromDB(CITY, { cityCodeSet: codes });
  return cities;
};

export const getAllIntlAirlines = async () => {
  const airlines = await getDocumentsFromDB(INTL_AIRLINE, {});
  return airlines;
};

export const getAirportByCodeCityCodeAndUrlPhrase = async (cityCode, airportCode, airportInUrl) => {
  const airport = await getDocumentFromDB(INTL_AIRPORT, {
    city_code: cityCode,
    code: airportCode,
    url_phrase: airportInUrl,
  });
  return airport;
};

export const getRedirectByRedirectAndHash = async (url, hash) => {
  const redirectObj = await getDocumentFromDB("intl_redirect", { redirect: url, hash });
  return redirectObj;
};

export const getAirportByOldAlias = async (oldAlias) => {
  const airport = await getDocumentFromDB(INTL_AIRPORT, { old_alias: oldAlias });
  return airport;
};

export const getIntlCountryByUrlPhraseForCountryPage = async (urlPhrase) => {
  const country = await getDocumentFromDB(INTL_COUNTRY, {
    url_phrase: urlPhrase,
    show_country_page: true,
  });
  return country;
};

export const getRedirectBySourceUrl = async (isIntl, sourceUrl) => {
  const table = isIntl ? "intl_redirect" : "flight_redirect";
  const redirect = await getDocumentFromDB(table, { source: sourceUrl });
  return redirect;
};

export const findByOldUrlAndEntityTypeAndLobAndCountryUrl = async (
  oldUrl,
  entityType,
  lob,
  countryUrl,
) => {
  const history = await getDocumentFromDB("flight_entity_history", {
    old_url: oldUrl,
    entity_type: entityType,
    lob,
    country_url: countryUrl,
  });
  return history;
};

export const getHistoryByOldUrlAndEntityTypeAndLob = async (isIntl, oldUrl, entityType, lob) => {
  const history = await getDocumentFromDB(isIntl ? "entity_history" : "flight_entity_history", {
    old_url: oldUrl,
    entity_type: entityType,
    lob,
  });
  return history;
};

export const getHistoryByOldUrlAndEntityTypeAndLobAndCityUrl = async (
  pathVar,
  entityType,
  lob,
  cityURL,
) => {
  const history = await getDocumentFromDB("entity_history", {
    old_url: pathVar,
    entity_type: entityType,
    lob,
    city_url: cityURL,
  });
  return history;
};

export const getAirlineSpecificFieldsByUrlPhrase = async (airlineName, airlineFieldName) => {
  let airline = await getDocumentFromDB(AIRLINE, { url_phrase: airlineName });
  if (!airline) {
    airline = await getDocumentFromDB(INTL_AIRLINE, { url_phrase: airlineName });
  }
  if (!airline) return null;
  const airlineFields = {};
  // eslint-disable-next-line camelcase
  const { name, code, url_phrase } = airline;
  airlineFields.name = name;
  airlineFields.code = code;
  // eslint-disable-next-line camelcase
  airlineFields.url_phrase = url_phrase;
  airlineFields[airlineFieldName] = airline[airlineFieldName];

  return airlineFields;
};

export const getRoutesBySrcAndPriority = async (cityCode, priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(
    DFROUTE,
    { src_city_code: cityCode, priority },
    "ASC",
    count,
  );
  return dfroutes;
};

export const getRoutesByDestAndPriority = async (cityCode, priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(
    DFROUTE,
    { dest_city_code: cityCode, priority },
    "ASC",
    count,
  );
  return dfroutes;
};

export const getRoutesByPriority = async (priority, count) => {
  const dfroutes = await getDocumentsByPriorityFromDB(DFROUTE, { priority }, "ASC", count);
  return dfroutes;
};

export const findByLobAndTemplateAndSitemapIndexAndLang = async (
  lob,
  templateName,
  index,
  lang,
) => {
  let siteMaps;
  if (lang) {
    siteMaps = await getDocumentFromDB("sitemaps", {
      lob,
      template: templateName,
      sitemap_index: index,
      lang,
    });
  } else {
    siteMaps = await getDocumentFromDB("sitemaps", {
      lob,
      template: templateName,
      sitemap_index: index,
    });
  }
  return siteMaps;
};

export const findByLobAndTemplateAndLang = async (lob, templateName, lang) => {
  let siteMaps;
  if (lang) {
    siteMaps = await getDocumentsFromDB("sitemaps", { lob, template: templateName, lang });
  } else {
    siteMaps = await getDocumentsFromDB("sitemaps", { lob, template: templateName });
  }
  return siteMaps;
};

export const findByLobAndTemplate = async (lob, templateName) => {
  let siteMaps = await getDocumentsFromDB("sitemaps", { lob, template: templateName });
  return siteMaps;
};

export const findAllByLob = async (lob) => {
  const siteMaps = await getDocumentsFromDB("sitemaps", { lob });
  return siteMaps;
};

export const removeSitemapsFromMongo = async (db, lob, templateName) => {
  const collection = db.collection("sitemaps");
  await collection.deleteMany({ lob, template: templateName });
};

export const saveSitemapInMongo = async (db, sitemapCollection, lob, index) => {
  try {
    const collection = db.collection("sitemaps");

    // Check for existing sitemap
    const existingSitemap = await collection.findOne({
      lob,
      template: sitemapCollection.template,
      sitemap_index: index,
      lang: sitemapCollection.lang,
    });
    if (existingSitemap) {
      // Update existing sitemap
      await collection.updateOne(
        {
          lob,
          template: sitemapCollection.template,
          sitemap_index: index,
          lang: sitemapCollection.lang,
        },
        {
          $set: {
            sitemaps: sitemapCollection.sitemaps.urls,
            sitemap_index: index,
          },
        },
      );
    } else {
      // Insert new sitemap
      await collection.insertOne(sitemapCollection);
    }
  } catch (error) {
    console.error("Error saving sitemap:", error);
  }
};

export const getDealsByLob = async (lob) => {
  const deals = await getDocumentsFromDB("DealsForChromeExtension", { lob });
  return deals;
};

export const getByConfiguration = async (configuration) => {
  const deals = await getDocumentsFromDB("ChromeExtensionTemplate", { configuration });
  return deals;
};
